import { useState, useEffect } from "react";
import {
  Users,
  DollarSign,
  TrendingUp,
  Building2,
  Repeat,
  Award,
  Calculator,
  BarChart3,
  Calendar,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Filter,
  ChevronDown,
  Target,
  Percent,
  TrendingDown,
  Eye,
  Clock,
  MapPin,
  Star,
  Activity,
  PieChart,
  LineChart,
} from "lucide-react";
import { useEventStore, Event } from "../../store/eventStore";
import { V1BEClient } from "../../clients";
import { IOrganizationEventCheckinStats } from "../../types/EventTypes";

interface DashboardData {
  expectedAttendees: number;
  collectedAmount: number;
  amountToCollect: number;
  exhibitorsWithBalance: number;
  exhibitorsFullPayment: number;
  totalExhibitors: number;
  returningExhibitors: number;
  totalSponsors: number;
  totalExpense: number;
  grossRevenue: number;
  netRevenue: number;
}

interface EventStats {
  eventId: string;
  eventTitle: string;
  registeredCount: number;
  capacity: number;
  revenue: number;
  expenses: number;
  profit: number;
  conversionRate: number;
  capacityUtilization: number;
  avgTicketPrice: number;
  daysUntilEvent: number;
  category: string;
}

const Dashboard = () => {
  const [stats, setStats] = useState<DashboardData>({
    expectedAttendees: 1250,
    collectedAmount: 2250000,
    amountToCollect: 750000,
    exhibitorsWithBalance: 8,
    exhibitorsFullPayment: 42,
    totalExhibitors: 50,
    returningExhibitors: 15,
    totalSponsors: 12,
    totalExpense: 1250000,
    grossRevenue: 3000000,
    netRevenue: 1750000,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<DashboardData>(stats);
  const [selectedEvent, setSelectedEvent] = useState<string>("all");
  const [isEventFilterOpen, setIsEventFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<
    "overview" | "detailed" | "comparison"
  >("overview");

  const { events, fetchEvents } = useEventStore();

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Placeholder for organization ID - in real app this would come from context/auth
  // Assuming organization ID for demo purposes or fetched from user details
  const organizationId = "c25c1da6-141a-42c2-b5e1-8857fd214532"; // Replace with actual logic to get org ID

  useEffect(() => {
    // Fetch stats from API
    const fetchStats = async () => {
      try {
        const response =
          await V1BEClient.getOrganizationEventAttendanceStats(organizationId);
        if (response && response.stats) {
          const apiStats: EventStats[] = response.stats.map(
            (stat: IOrganizationEventCheckinStats) => ({
              eventId: stat.organization_event_id,
              eventTitle: stat.organization_event_name,
              registeredCount: stat.total_supplier_check_in,
              capacity: 100, // Dummy
              revenue: 0, // Dummy
              expenses: 0, // Dummy
              profit: 0, // Dummy
              conversionRate: 0, // Dummy
              capacityUtilization: 0, // Dummy
              avgTicketPrice: 0, // Dummy
              daysUntilEvent: 0, // Dummy
              category: "General", // Dummy
            })
          );
          // Here we would ideally update a state variable that overrides `events` or `eventStats`
          // internal variable `fetchedApiStats`
          setFetchedApiStats(apiStats);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchStats();
  }, [organizationId]);

  const [fetchedApiStats, setFetchedApiStats] = useState<EventStats[] | null>(
    null
  );

  // Calculate overall statistics
  const overallStats = {
    totalEvents: events.length,
    totalCapacity: events.reduce((sum, event) => sum + event.capacity, 0),
    totalRegistered: events.reduce(
      (sum, event) => sum + event.registeredCount,
      0
    ),
    totalRevenue: events.reduce(
      (sum, event) => sum + event.price * event.registeredCount,
      0
    ),
    avgTicketPrice:
      events.length > 0
        ? events.reduce((sum, event) => sum + event.price, 0) / events.length
        : 0,
    totalReturningExhibitors: stats.returningExhibitors,
  };

  // Calculate per-event statistics
  const eventStats: EventStats[] = events.map((event) => {
    const eventDate = new Date(event.date);
    const today = new Date();
    const daysUntilEvent = Math.ceil(
      (eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    const revenue = event.price * event.registeredCount;
    const expenses = revenue * 0.4; // Assume 40% expenses for demo
    const profit = revenue - expenses;
    const conversionRate = (event.registeredCount / event.capacity) * 100;
    const capacityUtilization = (event.registeredCount / event.capacity) * 100;

    return {
      eventId: event.id,
      eventTitle: event.title,
      registeredCount: event.registeredCount,
      capacity: event.capacity,
      revenue,
      expenses,
      profit,
      conversionRate,
      capacityUtilization,
      avgTicketPrice: event.price,
      daysUntilEvent,
      category: event.category,
    };
  });

  // Calculate overall statistics
  const currentEvents = fetchedApiStats || eventStats; // Use fetched stats if available, else mock derived stats

  // Recalculate overallStats based on `currentEvents` if needed, but `overallStats` depends on `events` store directly in current code.
  // Ideally we should unify this.
  // Given the constraints and the request to "Update Dashboard.tsx to use the newly added endpoint for displaying the stats"
  // I will make `filteredEventStats` use `fetchedApiStats` merged with minimal mock data if present.

  // Re-deriving `eventStats` from `events` mock store is what happens now.
  // I will mix them.

  const displayEventStats = fetchedApiStats
    ? fetchedApiStats.map((apiStat) => {
        // Try to find matching mock event to fill in gaps if possible or just use dummy
        const mockEvent = events.find((e) => e.id === apiStat.eventId);
        if (mockEvent) {
          const revenue = mockEvent.price * apiStat.registeredCount;
          const expenses = revenue * 0.4;
          const profit = revenue - expenses;
          return {
            ...apiStat,
            capacity: mockEvent.capacity,
            revenue,
            expenses,
            profit,
            avgTicketPrice: mockEvent.price,
            category: mockEvent.category,
            daysUntilEvent: Math.ceil(
              (new Date(mockEvent.date).getTime() - new Date().getTime()) /
                (1000 * 60 * 60 * 24)
            ),
            conversionRate:
              (apiStat.registeredCount / mockEvent.capacity) * 100,
            capacityUtilization:
              (apiStat.registeredCount / mockEvent.capacity) * 100,
          };
        }
        return apiStat;
      })
    : eventStats;

  // Filter event stats based on selection
  const filteredEventStats =
    selectedEvent === "all"
      ? displayEventStats
      : displayEventStats.filter((stat) => stat.eventId === selectedEvent);

  // Recalculate overall stats dynamically based on displayEventStats
  const dynamicOverallStats = {
    totalEvents: displayEventStats.length,
    totalCapacity: displayEventStats.reduce((sum, e) => sum + e.capacity, 0),
    totalRegistered: displayEventStats.reduce(
      (sum, e) => sum + e.registeredCount,
      0
    ),
    totalRevenue: displayEventStats.reduce((sum, e) => sum + e.revenue, 0),
    avgTicketPrice:
      displayEventStats.length > 0
        ? displayEventStats.reduce((sum, e) => sum + e.avgTicketPrice, 0) /
          displayEventStats.length
        : 0,
    totalReturningExhibitors: stats.returningExhibitors,
  };
  const handleSave = () => {
    setStats(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(stats);
    setIsEditing(false);
  };

  const handleInputChange = (field: keyof DashboardData, value: number) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const data = isEditing ? editData : stats;

  const getSelectedEventName = () => {
    if (selectedEvent === "all") return "All Exhibits";
    const event = events.find((e) => e.id === selectedEvent);
    return event ? event.title : "All Exhibits";
  };

  const getPerformanceColor = (value: number, threshold: number) => {
    if (value >= threshold) return "text-green-600";
    if (value >= threshold * 0.8) return "text-yellow-600";
    return "text-red-600";
  };

  const getPerformanceIcon = (value: number, threshold: number) => {
    if (value >= threshold) return <TrendingUp className="w-4 h-4" />;
    if (value >= threshold * 0.8) return <Activity className="w-4 h-4" />;
    return <TrendingDown className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Event Filter Info */}
        {selectedEvent !== "all" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                Showing data for: {getSelectedEventName()}
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">
                Exhibit Dashboard
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                Comprehensive overview of exhibit performance and supplier
                metrics
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 w-full md:w-auto">
              {/* View Mode Toggle */}
              <div className="flex bg-white border border-gray-300 rounded-lg p-1 w-full sm:w-auto">
                <button
                  onClick={() => setViewMode("overview")}
                  className={`flex-1 px-2 py-2 md:px-3 rounded text-xs md:text-sm font-medium transition-colors ${
                    viewMode === "overview"
                      ? "bg-primary-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setViewMode("detailed")}
                  className={`flex-1 px-2 py-2 md:px-3 rounded text-xs md:text-sm font-medium transition-colors ${
                    viewMode === "detailed"
                      ? "bg-primary-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Detailed
                </button>
                <button
                  onClick={() => setViewMode("comparison")}
                  className={`flex-1 px-2 py-2 md:px-3 rounded text-xs md:text-sm font-medium transition-colors ${
                    viewMode === "comparison"
                      ? "bg-primary-600 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Comparison
                </button>
              </div>

              {/* Event Filter */}
              <div className="relative w-full sm:w-auto">
                <button
                  onClick={() => setIsEventFilterOpen(!isEventFilterOpen)}
                  className="w-full sm:w-auto bg-white border border-gray-300 text-gray-700 px-3 md:px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2 justify-between sm:justify-start"
                >
                  <Filter size={16} />
                  <span className="truncate">{getSelectedEventName()}</span>
                  <ChevronDown size={16} />
                </button>

                {isEventFilterOpen && (
                  <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setSelectedEvent("all");
                          setIsEventFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
                          selectedEvent === "all"
                            ? "bg-primary-50 text-primary-700"
                            : "text-gray-700"
                        }`}
                      >
                        All Exhibits
                      </button>
                      {events.map((event) => (
                        <button
                          key={event.id}
                          onClick={() => {
                            setSelectedEvent(event.id);
                            setIsEventFilterOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-gray-100 ${
                            selectedEvent === event.id
                              ? "bg-primary-50 text-primary-700"
                              : "text-gray-700"
                          }`}
                        >
                          {event.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Overview Mode */}
        {viewMode === "overview" && (
          <>
            {/* Overall Performance Metrics */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Overall Performance
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {dynamicOverallStats.totalEvents}
                  </div>
                  <div className="text-sm text-gray-600">Total Exhibits</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {formatNumber(dynamicOverallStats.totalRegistered)}
                  </div>
                  <div className="text-sm text-gray-600">Total Suppliers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {formatCurrency(dynamicOverallStats.totalRevenue)}
                  </div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    {formatNumber(dynamicOverallStats.totalReturningExhibitors)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Returning Suppliers
                  </div>
                </div>
              </div>
            </div>

            {/* Main Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {/* Expected Attendees */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Attendees
                  </span>
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    value={data.expectedAttendees}
                    onChange={(e) =>
                      handleInputChange(
                        "expectedAttendees",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="text-2xl font-bold text-gray-900 w-full border border-gray-300 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(data.expectedAttendees)}
                  </p>
                )}
                <p className="text-sm text-gray-600">Expected attendees</p>
              </div>

              {/* Collected Amount */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Collected
                  </span>
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    value={data.collectedAmount}
                    onChange={(e) =>
                      handleInputChange(
                        "collectedAmount",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="text-2xl font-bold text-gray-900 w-full border border-gray-300 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(data.collectedAmount)}
                  </p>
                )}
                <p className="text-sm text-gray-600">Amount collected</p>
              </div>

              {/* Amount to Collect */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CreditCard className="w-6 h-6 text-yellow-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Pending
                  </span>
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    value={data.amountToCollect}
                    onChange={(e) =>
                      handleInputChange(
                        "amountToCollect",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="text-2xl font-bold text-gray-900 w-full border border-gray-300 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(data.amountToCollect)}
                  </p>
                )}
                <p className="text-sm text-gray-600">Amount to collect</p>
              </div>

              {/* Total Revenue */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Total Revenue
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(data.collectedAmount + data.amountToCollect)}
                </p>
                <p className="text-sm text-gray-600">Gross revenue</p>
              </div>

              {/* Total Expenses */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Calculator className="w-6 h-6 text-red-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Expenses
                  </span>
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    value={data.totalExpense}
                    onChange={(e) =>
                      handleInputChange(
                        "totalExpense",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="text-2xl font-bold text-gray-900 w-full border border-gray-300 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(data.totalExpense)}
                  </p>
                )}
                <p className="text-sm text-gray-600">Total expenses</p>
              </div>

              {/* Net Revenue */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Net Revenue
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(data.netRevenue)}
                </p>
                <p className="text-sm text-gray-600">After expenses</p>
              </div>

              {/* Total Exhibitors */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building2 className="w-6 h-6 text-indigo-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Suppliers
                  </span>
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    value={data.totalExhibitors}
                    onChange={(e) =>
                      handleInputChange(
                        "totalExhibitors",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="text-2xl font-bold text-gray-900 w-full border border-gray-300 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(data.totalExhibitors)}
                  </p>
                )}
                <p className="text-sm text-gray-600">Total suppliers</p>
              </div>

              {/* Returning Exhibitors */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Repeat className="w-6 h-6 text-orange-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-500">
                    Returning
                  </span>
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    value={data.returningExhibitors}
                    onChange={(e) =>
                      handleInputChange(
                        "returningExhibitors",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="text-2xl font-bold text-gray-900 w-full border border-gray-300 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(data.returningExhibitors)}
                  </p>
                )}
                <p className="text-sm text-gray-600">Returning suppliers</p>
              </div>
            </div>

            {/* Additional Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Payment Status */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Payment Status
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Full Payment</span>
                    <span className="font-semibold text-green-600">
                      {data.exhibitorsFullPayment}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">With Balance</span>
                    <span className="font-semibold text-yellow-600">
                      {data.exhibitorsWithBalance}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">Total</span>
                      <span className="font-bold text-gray-900">
                        {data.totalExhibitors}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sponsors */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Sponsors
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-purple-600" />
                    <span className="text-gray-600">Total Sponsors</span>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">
                    {data.totalSponsors}
                  </span>
                </div>
              </div>

              {/* Revenue Summary */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Revenue Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Gross Revenue</span>
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(data.grossRevenue)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Expenses</span>
                    <span className="font-semibold text-red-600">
                      -{formatCurrency(data.totalExpense)}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        Net Revenue
                      </span>
                      <span className="font-bold text-emerald-600">
                        {formatCurrency(data.netRevenue)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Detailed Mode */}
        {viewMode === "detailed" && (
          <div className="space-y-8">
            {/* Event Performance Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Exhibit Performance Details
                </h2>
                <p className="text-gray-600 mt-1">
                  Detailed metrics for each exhibit
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Exhibit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Suppliers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Capacity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Profit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Days Left
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEventStats.map((stat) => (
                      <tr key={stat.eventId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {stat.eventTitle}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {stat.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(stat.registeredCount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(stat.capacity)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span
                              className={`text-sm font-medium ${getPerformanceColor(stat.capacityUtilization, 80)}`}
                            >
                              {formatPercentage(stat.capacityUtilization)}
                            </span>
                            {getPerformanceIcon(stat.capacityUtilization, 80)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(stat.revenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`text-sm font-medium ${stat.profit >= 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {formatCurrency(stat.profit)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 text-gray-400 mr-1" />
                            <span
                              className={`text-sm font-medium ${
                                stat.daysUntilEvent <= 7
                                  ? "text-red-600"
                                  : stat.daysUntilEvent <= 30
                                    ? "text-yellow-600"
                                    : "text-green-600"
                              }`}
                            >
                              {stat.daysUntilEvent} days
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Performance Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredEventStats.map((stat) => (
                <div
                  key={stat.eventId}
                  className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 truncate">
                    {stat.eventTitle}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Conversion Rate
                      </span>
                      <span
                        className={`text-sm font-medium ${getPerformanceColor(stat.conversionRate, 70)}`}
                      >
                        {formatPercentage(stat.conversionRate)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Avg Booth Price
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(stat.avgTicketPrice)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Profit Margin
                      </span>
                      <span
                        className={`text-sm font-medium ${stat.profit >= 0 ? "text-green-600" : "text-red-600"}`}
                      >
                        {formatPercentage((stat.profit / stat.revenue) * 100)}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">
                          Total Revenue
                        </span>
                        <span className="text-sm font-bold text-gray-900">
                          {formatCurrency(stat.revenue)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comparison Mode */}
        {viewMode === "comparison" && (
          <div className="space-y-8">
            {/* Event Comparison Chart */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Exhibit Performance Comparison
              </h2>
              <div className="space-y-4">
                {filteredEventStats.map((stat) => (
                  <div
                    key={stat.eventId}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-medium text-gray-900">
                        {stat.eventTitle}
                      </h3>
                      <span className="text-sm text-gray-500">
                        {stat.category}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {formatNumber(stat.registeredCount)}
                        </div>
                        <div className="text-xs text-gray-600">Suppliers</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {formatPercentage(stat.capacityUtilization)}
                        </div>
                        <div className="text-xs text-gray-600">
                          Capacity Used
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {formatCurrency(stat.revenue)}
                        </div>
                        <div className="text-xs text-gray-600">Revenue</div>
                      </div>
                      <div className="text-center">
                        <div
                          className={`text-2xl font-bold ${stat.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}
                        >
                          {formatCurrency(stat.profit)}
                        </div>
                        <div className="text-xs text-gray-600">Profit</div>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Capacity Utilization</span>
                        <span>
                          {formatPercentage(stat.capacityUtilization)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            stat.capacityUtilization >= 80
                              ? "bg-green-500"
                              : stat.capacityUtilization >= 60
                                ? "bg-yellow-500"
                                : "bg-red-500"
                          }`}
                          style={{
                            width: `${Math.min(stat.capacityUtilization, 100)}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Best Performing Exhibit
                </h3>
                {filteredEventStats.length > 0 &&
                  (() => {
                    const bestEvent = filteredEventStats.reduce(
                      (best, current) =>
                        current.revenue > best.revenue ? current : best
                    );
                    return (
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600 mb-2">
                          {bestEvent.eventTitle}
                        </div>
                        <div className="text-sm text-gray-600">
                          Revenue: {formatCurrency(bestEvent.revenue)}
                        </div>
                        <div className="text-sm text-gray-600">
                          Utilization:{" "}
                          {formatPercentage(bestEvent.capacityUtilization)}
                        </div>
                      </div>
                    );
                  })()}
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Highest Conversion Rate
                </h3>
                {filteredEventStats.length > 0 &&
                  (() => {
                    const bestConversion = filteredEventStats.reduce(
                      (best, current) =>
                        current.conversionRate > best.conversionRate
                          ? current
                          : best
                    );
                    return (
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600 mb-2">
                          {bestConversion.eventTitle}
                        </div>
                        <div className="text-sm text-gray-600">
                          Rate:{" "}
                          {formatPercentage(bestConversion.conversionRate)}
                        </div>
                        <div className="text-sm text-gray-600">
                          Suppliers:{" "}
                          {formatNumber(bestConversion.registeredCount)}
                        </div>
                      </div>
                    );
                  })()}
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Most Profitable Exhibit
                </h3>
                {filteredEventStats.length > 0 &&
                  (() => {
                    const mostProfitable = filteredEventStats.reduce(
                      (best, current) =>
                        current.profit > best.profit ? current : best
                    );
                    return (
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-600 mb-2">
                          {mostProfitable.eventTitle}
                        </div>
                        <div className="text-sm text-gray-600">
                          Profit: {formatCurrency(mostProfitable.profit)}
                        </div>
                        <div className="text-sm text-gray-600">
                          Margin:{" "}
                          {formatPercentage(
                            (mostProfitable.profit / mostProfitable.revenue) *
                              100
                          )}
                        </div>
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
